import { useEffect, useRef, useState } from "react";
import "../component css/CustomCursor.css";
import ringCursor from "../assets/images/ring.svg";

const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "input",
  "textarea",
  "select",
  "label",
  "[role='button']",
  "[data-cursor='interactive']",
].join(", ");

function CustomCursor() {
  const ringRef = useRef(null);
  const animationFrameRef = useRef(null);
  const hasMovedRef = useRef(false);
  const pointerPositionRef = useRef({ x: 0, y: 0 });
  const ringPositionRef = useRef({ x: 0, y: 0 });
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [interactive, setInteractive] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(pointer: fine)");

    const updateEnabledState = () => {
      setEnabled(mediaQuery.matches);
    };

    updateEnabledState();
    mediaQuery.addEventListener("change", updateEnabledState);

    return () => {
      mediaQuery.removeEventListener("change", updateEnabledState);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setVisible(false);
      setInteractive(false);
      return undefined;
    }

    const ringElement = ringRef.current;

    if (!ringElement) {
      return undefined;
    }

    const animateRing = () => {
      const nextX = ringPositionRef.current.x + (pointerPositionRef.current.x - ringPositionRef.current.x) * 0.18;
      const nextY = ringPositionRef.current.y + (pointerPositionRef.current.y - ringPositionRef.current.y) * 0.18;

      ringPositionRef.current = { x: nextX, y: nextY };
      ringElement.style.transform = `translate3d(${nextX}px, ${nextY}px, 0) translate(-50%, -50%)`;
      animationFrameRef.current = window.requestAnimationFrame(animateRing);
    };

    const handlePointerMove = (event) => {
      const { clientX, clientY, target } = event;
      pointerPositionRef.current = { x: clientX, y: clientY };

      if (!hasMovedRef.current) {
        ringPositionRef.current = { x: clientX, y: clientY };
        ringElement.style.transform = `translate3d(${clientX}px, ${clientY}px, 0) translate(-50%, -50%)`;
        hasMovedRef.current = true;
      }

      setVisible(true);
      setInteractive(Boolean(target.closest(INTERACTIVE_SELECTOR)));
    };

    const handlePointerLeave = () => {
      hasMovedRef.current = false;
      setVisible(false);
      setInteractive(false);
    };

    animationFrameRef.current = window.requestAnimationFrame(animateRing);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerleave", handlePointerLeave);
    window.addEventListener("blur", handlePointerLeave);

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerleave", handlePointerLeave);
      window.removeEventListener("blur", handlePointerLeave);
    };
  }, [enabled]);

  // Always mounted (never conditionally returns null) — the ring stays
  // invisible via CSS (opacity: 0 by default, plus a display:none media
  // query for coarse pointers) instead of being inserted/removed from the
  // tree. It doesn't need `enabled` to gate the DOM node, only to gate
  // whether the tracking effect above does anything.
  return (
    <div
      ref={ringRef}
      className={`custom-cursor-ring${visible ? " is-visible" : ""}${interactive ? " is-interactive" : ""}`}
      aria-hidden="true"
    >
      <img src={ringCursor} alt="" draggable="false" />
    </div>
  );
}

export default CustomCursor;